import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  ShieldCheck, Wrench, LifeBuoy, FileCheck2, BadgePercent, Plus, Trash2, RotateCcw,
  Loader2, Save, Sparkles, AlertTriangle, Pencil, Check, X, FileSignature,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { loanProfilingApi } from '@/services/api';
import { inr } from './CockpitUI';
import { DocLink } from './OfferPieces';

const ICONS = {
  engine_warranty: ShieldCheck,
  doorstep_service: Wrench,
  insurance_claim_assist: FileCheck2,
  roadside_assistance: LifeBuoy,
  processing_fee_waiver: BadgePercent,
};

const KIND_LABELS = { DURATION: 'Lasts for', COUNT: 'How many', FLAG: 'Included as' };
const KINDS = ['DURATION', 'COUNT', 'FLAG'];
const KIND_WORDS = { DURATION: 'Duration', COUNT: 'Count', FLAG: 'Yes / no' };
const title = (s) => (s ? KIND_WORDS[s] || s.charAt(0) + s.slice(1).toLowerCase() : '');

// Mirrors the server's sentence builder so the rep sees the letter change as he types.
const unitWord = (q, u) => {
  const w = (u || '').toLowerCase();
  return Number(q) === 1 && w.endsWith('s') ? w.slice(0, -1) : w;
};
const letterText = (i) => {
  if (i.kind === 'DURATION' && i.quantity) return `${i.quantity} ${unitWord(i.quantity, i.unit || 'DAYS')}${i.charge === 'PAID' ? ' (chargeable)' : ''}`;
  if (i.kind === 'COUNT' && i.quantity) return `${i.quantity} ${i.charge === 'FREE' ? 'free' : 'paid'} ${unitWord(i.quantity, i.service_type || 'SERVICES')}`;
  return i.charge === 'FREE' ? 'Free' : i.charge === 'PAID' ? 'Chargeable' : 'Included';
};

const Pick = ({ value, onChange, options, testId, width = 'w-[116px]' }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className={`h-9 text-sm ${width}`} data-testid={testId}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => <SelectItem key={o} value={o} data-testid={`${testId}-${o}`}>{title(o)}</SelectItem>)}
    </SelectContent>
  </Select>
);

// Number + unit + free/paid — never a sentence a rep has to retype correctly.
const BenefitCard = ({ item, options, onChange, onRemove }) => {
  const Icon = ICONS[item.key] || Sparkles;
  const on = !!item.included;
  return (
    <div className={`rounded-2xl border p-4 transition-colors ${on ? 'border-slate-200 bg-white shadow-sm' : 'border-dashed border-slate-200 bg-slate-50/60'}`}
      data-testid={`benefit-row-${item.key}`}>
      <div className="flex items-start gap-3">
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${on ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <Input value={item.label} onChange={(e) => onChange({ label: e.target.value })}
            className="h-9 text-sm font-semibold border-transparent hover:border-slate-200 focus:border-slate-300 px-2 -ml-2"
            placeholder="What the customer gets" data-testid={`benefit-label-${item.key}`} />
          <Input value={item.detail} onChange={(e) => onChange({ detail: e.target.value })}
            className="h-7 text-xs text-slate-500 border-transparent hover:border-slate-200 focus:border-slate-300 px-2 -ml-2"
            placeholder="One line the customer reads under it" data-testid={`benefit-detail-${item.key}`} />
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Switch checked={on} onCheckedChange={(v) => onChange({ included: v })}
            data-testid={`benefit-toggle-${item.key}`} />
          {item.custom && (
            <Button size="sm" variant="ghost" onClick={onRemove} className="text-rose-600 hover:bg-rose-50 h-8 px-2"
              data-testid={`benefit-remove-${item.key}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 pl-12 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mr-1">
          {KIND_LABELS[item.kind] || 'Included as'}
        </span>
        {item.custom && (
          <Pick value={item.kind} options={options.kinds || KINDS}
            onChange={(v) => onChange({
              kind: v,
              quantity: v === 'FLAG' ? null : (item.quantity || 1),
              unit: v === 'DURATION' ? (item.unit || 'MONTHS') : null,
              service_type: v === 'COUNT' ? (item.service_type || 'SERVICES') : null,
            })}
            testId={`benefit-kind-${item.key}`} width="w-[112px]" />
        )}
        {item.kind !== 'FLAG' && (
          <Input type="number" min="0" value={item.quantity ?? ''} placeholder="0"
            onChange={(e) => onChange({ quantity: e.target.value === '' ? null : Number(e.target.value) })}
            className="h-9 w-[76px] text-sm" data-testid={`benefit-quantity-${item.key}`} />
        )}
        {item.kind === 'DURATION' && (
          <Pick value={item.unit} onChange={(v) => onChange({ unit: v })} options={options.units}
            testId={`benefit-unit-${item.key}`} />
        )}
        {item.kind === 'COUNT' && (
          <Pick value={item.service_type} onChange={(v) => onChange({ service_type: v })} options={options.service_types}
            testId={`benefit-servicetype-${item.key}`} width="w-[140px]" />
        )}
        <Pick value={item.charge} onChange={(v) => onChange({ charge: v })} options={options.charges}
          testId={`benefit-charge-${item.key}`} width="w-[96px]" />
        <div className="relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">worth ₹</span>
          <Input type="number" min="0" value={item.worth ?? 0}
            onChange={(e) => onChange({ worth: e.target.value === '' ? 0 : Number(e.target.value) })}
            className="h-9 w-[132px] text-sm pl-[54px]" data-testid={`benefit-worth-${item.key}`} />
        </div>
        <span className="text-xs text-slate-500 w-full xl:w-auto xl:ml-auto" data-testid={`benefit-preview-${item.key}`}>
          Letter reads: <strong className="text-slate-700">{letterText(item)}</strong>
        </span>
      </div>
    </div>
  );
};

const Threshold = ({ value, onSave, busy }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} className="underline decoration-dotted underline-offset-4 hover:text-blue-700"
        data-testid="benefits-threshold-edit">
        {inr(value)}<Pencil className="w-3 h-3 inline ml-1 mb-0.5" />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <Input type="number" min="0" value={draft} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
        className="h-8 w-32 text-sm" autoFocus data-testid="benefits-threshold-input" />
      <Button size="sm" className="h-8 px-2 bg-blue-600 hover:bg-blue-700" disabled={busy || draft === '' || Number.isNaN(Number(draft))}
        onClick={async () => { if (await onSave(Number(draft)) !== false) setEditing(false); }} data-testid="benefits-threshold-save">
        <Check className="w-3.5 h-3.5" />
      </Button>
      <Button size="sm" variant="ghost" className="h-8 px-2" disabled={busy}
        onClick={() => { setDraft(value); setEditing(false); }} data-testid="benefits-threshold-cancel">
        <X className="w-3.5 h-3.5" />
      </Button>
    </span>
  );
};

// The certificate the customer keeps — generated off the saved package, kept as
// versions because the package gets edited after a customer has one in hand.
const BenefitsCertificate = ({ leadId, letters, dirty, count, onGenerated }) => {
  const [busy, setBusy] = useState(false);

  const make = async () => {
    setBusy(true);
    try {
      const res = await loanProfilingApi.generateBenefitsLetter(leadId);
      onGenerated(res.data.package);
      toast.success(`Benefits certificate v${res.data.letter.version} ready`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not build the certificate');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="benefits-certificate">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">Benefits certificate</h4>
          <p className="text-[11px] text-slate-400 mt-0.5 max-w-xl">
            A standalone letter with only the warranty and service package — what he gets, what it is worth
            and how to claim it. The customer keeps this one.
          </p>
        </div>
        <Button size="sm" variant="outline" disabled={busy || dirty || !count} onClick={make}
          data-testid="benefits-letter-generate">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSignature className="w-3.5 h-3.5 mr-1.5" />}
          {letters.length ? 'Generate again' : 'Generate certificate'}
        </Button>
      </div>

      {dirty && (
        <p className="text-[11px] text-amber-700 mt-2" data-testid="benefits-letter-dirty">
          Save the package first — the certificate prints what is saved.
        </p>
      )}

      {letters.length === 0 ? (
        <p className="text-xs text-slate-400 mt-2" data-testid="benefits-letter-empty">
          No certificate yet for this customer.
        </p>
      ) : (
        <ul className="mt-3 space-y-1.5" data-testid="benefits-letter-versions">
          {[...letters].reverse().map((l) => (
            <li key={l.version} className="flex flex-wrap items-center gap-3 text-xs text-slate-500"
              data-testid={`benefits-letter-${l.version}`}>
              <span className="font-semibold text-slate-700">v{l.version}</span>
              <span className="text-slate-400">{l.letter_no}</span>
              <span>{l.benefits_count} benefits{l.total_worth > 0 && <> · {inr(l.total_worth)}</>}</span>
              <DocLink leadId={leadId} docId={l.doc_id} name="Download PDF"
                testId={`benefits-letter-download-${l.version}`} />
              <span className="text-slate-400 ml-auto">
                {new Date(l.generated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} · {l.generated_by}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const WarrantyServiceOffers = ({ leadId, onChanged }) => {
  const [pkg, setPkg] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const dirtyRef = useRef(false);

  const touch = (updater) => {
    setItems(updater);
    dirtyRef.current = true;
  };

  const load = useCallback(async () => {
    try {
      const res = await loanProfilingApi.serviceBenefits(leadId);
      setPkg(res.data.package);
      if (!dirtyRef.current) setItems(res.data.package.items);
    } catch (err) {
      toast.error('Could not load the package');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const settle = (pack) => {
    setPkg(pack);
    setItems(pack.items);
    dirtyRef.current = false;
  };

  const patch = (idx, p) => touch((prev) => prev.map((it, i) => (i === idx ? { ...it, ...p } : it)));
  const remove = (idx) => touch((prev) => prev.filter((_, i) => i !== idx));
  const addCustom = () => touch((prev) => [...prev, {
    key: `custom_${Date.now()}`, label: '', detail: '', kind: 'COUNT', quantity: 1,
    service_type: 'SERVICES', charge: 'FREE', worth: 0, included: true, custom: true, value_text: '',
  }]);

  const save = async () => {
    if (items.some((i) => !i.label.trim())) {
      toast.error('Name every benefit before saving');
      return;
    }
    setBusy(true);
    try {
      const res = await loanProfilingApi.saveServiceBenefits(leadId, { items });
      settle(res.data.package);
      toast.success('Saved — it prints on every letter and the customer can claim it');
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(`Save failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const saveThreshold = async (amount) => {
    setBusy(true);
    try {
      const res = await loanProfilingApi.setBenefitThreshold(leadId, amount);
      setPkg(res.data.package);
      if (!dirtyRef.current) setItems(res.data.package.items);
      toast.success(`Full package now starts at ${inr(amount)}`);
      if (onChanged) await onChanged();
      return true;
    } catch (err) {
      toast.error(`Could not save the cut-off: ${err?.response?.data?.detail || err.message}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Go back to the standard package for this loan amount?')) return;
    setBusy(true);
    try {
      const res = await loanProfilingApi.resetServiceBenefits(leadId);
      settle(res.data.package);
      toast.success('Back to the standard package');
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(`Could not reset it: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="py-16 flex justify-center" data-testid="benefits-loading"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  const options = pkg?.options || { units: [], service_types: [], charges: [] };
  const included = items.filter((i) => i.included);
  const worth = included.reduce((s, i) => s + Number(i.worth || 0), 0);
  const full = pkg?.tier === 'FULL';
  // what is on screen against what is saved — toggling a benefit off and back on
  // is not an edit, and the certificate button must not stay blocked for it
  const dirty = JSON.stringify(items.map((i) => ({ ...i, value_text: undefined })))
    !== JSON.stringify((pkg?.items || []).map((i) => ({ ...i, value_text: undefined })));

  return (
    <div data-testid="warranty-service-offers">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-slate-900">Warranty & Service Offers</h3>
          <p className="text-sm text-slate-500 mt-0.5 max-w-3xl">
            A bank cannot sell a warranty and a garage cannot lend — this is the package only Wisedrive can put in
            one envelope. It prints on every offer letter, and the customer can claim each line from his app.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={reset} data-testid="benefits-reset-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Standard package
          </Button>
          <Button disabled={!dirty || busy} onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="benefits-save-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            {dirty ? 'Save package' : 'Saved'}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-2.5 mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-700"
        data-testid="benefits-basis">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${full ? 'bg-blue-600 text-white' : 'bg-amber-100 text-amber-800'}`}
          data-testid="benefits-tier">
          {full ? 'Full package' : 'Smaller package'}
        </span>
        <span>
          {pkg?.basis_amount
            ? <>Set on <strong>{inr(pkg.basis_amount)}</strong> — {pkg.basis_source}</>
            : <>No loan amount on the file yet</>}
        </span>
        <span className="text-slate-600">
          Full package from <Threshold value={pkg?.full_package_from} onSave={saveThreshold} busy={busy} />
        </span>
        <span className="ml-auto font-medium text-slate-900" data-testid="benefits-total">
          {included.length} on the letter{worth > 0 && <> · worth {inr(worth)}</>}
        </span>
      </div>

      {pkg?.slab_changed && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 mb-4 flex items-start gap-2.5 text-sm text-amber-900"
          data-testid="benefits-slab-changed">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {pkg.slab_change_reason === 'CUTOFF'
              ? <>The cut-off has moved, so this file is now on the <strong>{full ? 'full' : 'smaller'}</strong> package,
                  but these lines were set for the {pkg.saved_tier === 'FULL' ? 'full' : 'smaller'} one.</>
              : <>The loan amount has since moved to the <strong>{full ? 'full' : 'smaller'}</strong> package,
                  but these lines were set for the {pkg.saved_tier === 'FULL' ? 'full' : 'smaller'} one.</>}
            {' '}Check them, or go back to standard.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {items.map((item, idx) => (
          <BenefitCard key={item.key} item={item} options={options}
            onChange={(p) => patch(idx, p)} onRemove={() => remove(idx)} />
        ))}
        <button onClick={addCustom}
          className="rounded-2xl border border-dashed border-slate-300 text-sm text-slate-500 hover:text-blue-700 hover:border-blue-300 py-6 transition-colors"
          data-testid="benefits-add-btn">
          <Plus className="w-4 h-4 inline mr-1.5" />Add a benefit for this customer
        </button>
      </div>

      <BenefitsCertificate leadId={leadId} letters={pkg?.letters || []} dirty={dirty}
        count={included.length} onGenerated={(pack) => setPkg(pack)} />
    </div>
  );
};

export default WarrantyServiceOffers;
